import {
	getBenefitConsumptionPercentageLabel,
	getBenefitConsumptionProgress,
	getBenefitRemainingBalance,
	hasBenefitUsageCredit,
} from '@/utils/clubBenefitUsage';

export const sortClubBenefitsByPriority = (benefits?: any[]) =>
	[...(benefits ?? [])].sort(
		(a, b) => (b?.display_order ?? 0) - (a?.display_order ?? 0),
	);

export type ClubProcessedBenefit = {
	id: string;
	title: string;
	description: string;
	used: number;
	total: number;
	remaining: number;
	progressPercentage: number;
	hasUsageCredit: boolean;
	renewalLabel: string;
	renewalTrigger: string;
	serviceType?: string;
	serviceOrigin?: string;
	serviceId?: string;
};

export type ClubMonthlySummary = {
	globalUsed: number;
	globalTotal: number;
	globalConsumptionLabel: number;
	globalConsumptionProgress: number;
	globalHasUsageCredit: boolean;
	startDate: string;
};

type UserClubSubscription = {
	start_date?: string;
	userClubSubscriptionBenefits?: Array<{
		club_benefit_id: string;
		used_count?: number;
	}>;
};

type ClubWithBenefits = {
	benefits?: any[];
};

export function processClubBenefitsForUser(
	userSubscription: UserClubSubscription,
	currentClub: ClubWithBenefits | null | undefined,
): {
	processedBenefits: ClubProcessedBenefit[];
	monthlySummary: ClubMonthlySummary;
} {
	let globalUsed = 0;
	let globalTotal = 0;

	const benefitsForUser = sortClubBenefitsByPriority(
		currentClub?.benefits ?? [],
	).filter((benefit: any) => benefit.service?.type !== 'INFORMATION');

	const processedBenefits = benefitsForUser.map((benefit: any) => {
		const userBenefitUsage =
			userSubscription.userClubSubscriptionBenefits?.find(
				(b) => b.club_benefit_id === benefit.id,
			);

		const used = userBenefitUsage?.used_count ?? 0;
		const total = benefit.default_amount ?? 0;

		let renewalLabel = '';
		let shouldCountInGlobalStats = false;

		switch (benefit.renewal_trigger) {
			case 'ALWAYS_ON_SUBSCRIPTION_PAYMENT':
				renewalLabel = 'Renova Mensalmente';
				shouldCountInGlobalStats = true;
				break;
			case 'NEVER':
				renewalLabel = 'Uso Único';
				shouldCountInGlobalStats = true;
				break;
			case 'ALWAYS_ON_USE':
				renewalLabel = 'Sempre Disponível';
				shouldCountInGlobalStats = false;
				break;
			default:
				renewalLabel = 'Renovação Variável';
				shouldCountInGlobalStats = false;
		}

		if (shouldCountInGlobalStats) {
			globalUsed += used;
			globalTotal += total;
		}

		return {
			id: benefit.id,
			title:
				benefit.service_substitute_title ||
				benefit.service?.service_name ||
				'',
			description: benefit.service?.service_name || '',
			used,
			total,
			remaining: getBenefitRemainingBalance(total, used),
			progressPercentage: getBenefitConsumptionProgress(total, used),
			hasUsageCredit: hasBenefitUsageCredit(total, used),
			renewalLabel,
			renewalTrigger: benefit.renewal_trigger,
			serviceType: benefit.service?.type,
			serviceOrigin: benefit.service?.origin,
			serviceId: benefit.service?.id || benefit.service_id,
		};
	});

	const globalConsumptionLabel =
		globalTotal > 0
			? getBenefitConsumptionPercentageLabel(globalTotal, globalUsed)
			: 0;
	const globalConsumptionProgress =
		globalTotal > 0
			? getBenefitConsumptionProgress(globalTotal, globalUsed)
			: 0;
	const globalHasUsageCredit = globalUsed < 0;
	const startDate = userSubscription.start_date
		? new Date(userSubscription.start_date).toLocaleDateString('pt-BR', {
				day: 'numeric',
				month: 'long',
		  })
		: '--';

	return {
		processedBenefits,
		monthlySummary: {
			globalUsed,
			globalTotal,
			globalConsumptionLabel,
			globalConsumptionProgress,
			globalHasUsageCredit,
			startDate,
		},
	};
}
